import React from 'react';
import { useQuery } from 'react-query';

const ACTION_CONFIG = {
  CREATE: { color: 'bg-green-500', label: 'Created' },
  UPDATE: { color: 'bg-blue-500', label: 'Updated' },
  DELETE: { color: 'bg-red-500', label: 'Deleted' },
  STATUS_CHANGE: { color: 'bg-yellow-500', label: 'Status Changed' },
  LOGIN: { color: 'bg-purple-500', label: 'Login' },
  LOGOUT: { color: 'bg-gray-500', label: 'Logout' },
};

const parseTimestamp = (timestamp) => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatTimestamp = (timestamp) => {
  const date = parseTimestamp(timestamp);
  if (!date) return 'Unknown time';
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
};

const formatFullTimestamp = (timestamp) => {
  const date = parseTimestamp(timestamp);
  if (!date) return 'Unknown time';

  return date.toLocaleString('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

/**
 * Reusable activity timeline component.
 *
 * Props:
 *   fetchFn - async function(params) => { data: [...], total, page, totalPages }
 *   entityType - filter by entity type (e.g., 'InventoryItem')
 *   entityId - filter by specific entity
 *   limit - number of entries to show (default 10)
 *   title - section title (default "Activity")
 *   showEntityInfo - show entity type/id column (default false, useful for global views)
 */
const ActivityTimeline = ({ fetchFn, entityType, entityId, limit = 10, title = 'Activity', showEntityInfo = false }) => {
  const queryKey = ['activity-timeline', entityType, entityId, limit];

  const { data, isLoading } = useQuery(
    queryKey,
    () => fetchFn({ entityType, entityId, limit, page: 1 }),
    { enabled: !!fetchFn, refetchInterval: 30000 }
  );

  const entries = data?.data || [];

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-start space-x-3">
            <div className="w-7 h-7 rounded-full bg-gray-200"></div>
            <div className="flex-1 space-y-1">
              <div className="h-3 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-4 text-center">
        No activity recorded yet.
      </div>
    );
  }

  return (
    <div>
      {title && <h4 className="text-sm font-semibold text-gray-900 mb-3">{title}</h4>}
      <div className="flow-root">
        <ul className="-mb-4">
          {entries.map((entry, index) => {
            const config = ACTION_CONFIG[entry.action] || ACTION_CONFIG.UPDATE;
            const isLast = index === entries.length - 1;

            return (
              <li key={entry.id}>
                <div className="relative pb-4">
                  {!isLast && (
                    <span className="absolute left-3.5 top-7 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                  )}
                  <div className="relative flex items-start space-x-3">
                    <div className="relative flex h-7 w-7 items-center justify-center">
                      <span className={`h-2.5 w-2.5 rounded-full ${config.color}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-gray-900">
                        {entry.details || `${config.label} ${entry.entityType}`}
                      </div>
                      <div className="mt-0.5 flex items-center space-x-2 text-xs text-gray-500">
                        <span>{entry.userName || 'System'}</span>
                        <span title={formatFullTimestamp(entry.timestamp)}>
                          {formatTimestamp(entry.timestamp)}
                        </span>
                      </div>
                      {entry.changes && Object.keys(entry.changes).length > 0 && (
                        <div className="mt-1 text-xs text-gray-400">
                          {Object.entries(entry.changes).map(([field, change]) => (
                            <span key={field} className="mr-3">
                              {field}: {typeof change === 'object' && change.from !== undefined
                                ? `${change.from} → ${change.to}`
                                : JSON.stringify(change)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default ActivityTimeline;
